import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Ensure API URL always ends with /api
  const getApiUrl = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  };
  const API = getApiUrl();

  // Setup axios interceptor to add auth token to all requests
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const authToken = localStorage.getItem('auth_token');
        if (authToken) {
          config.headers.Authorization = `Bearer ${authToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Cleanup interceptor on unmount
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  useEffect(() => {
    // Check for stored token and validate
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('user_profile');
    const lastActivity = localStorage.getItem('last_activity');
    
    // Session timeout: 24 hours of inactivity
    const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    if (storedToken && storedUser) {
      // Check if session has expired
      if (lastActivity) {
        const timeSinceActivity = Date.now() - parseInt(lastActivity);
        if (timeSinceActivity > SESSION_TIMEOUT) {
          // Session expired, clear everything
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_profile');
          localStorage.removeItem('last_activity');
          setLoading(false);
          return;
        }
      }
      
      setToken(storedToken);
      const user = JSON.parse(storedUser);
      setCurrentUser({ uid: user.uid, email: user.email });
      fetchProfile(storedToken);
      
      // Update last activity
      localStorage.setItem('last_activity', Date.now().toString());
    } else {
      setLoading(false);
    }

    // Activity listener to track user interactions
    const updateActivity = () => {
      if (storedToken) {
        localStorage.setItem('last_activity', Date.now().toString());
      }
    };

    // Listen to user activity
    window.addEventListener('click', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('scroll', updateActivity);

    return () => {
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, []);

  const fetchProfile = async (authToken) => {
    try {
      const response = await axios.get(`${API}/simple-auth/profile`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUserProfile(response.data);
      setCurrentUser({ uid: response.data.uid, email: response.data.email });
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Token might be invalid, clear it
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_profile');
      setToken(null);
      setCurrentUser(null);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email, password, name) => {
    try {
      const response = await axios.post(`${API}/simple-auth/signup`, {
        name,
        email,
        password
      });
      
      const { token: authToken, user } = response.data;
      
      // Store token, user, and activity timestamp
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('user_profile', JSON.stringify(user));
      localStorage.setItem('last_activity', Date.now().toString());
      
      setToken(authToken);
      setCurrentUser({ uid: user.uid, email: user.email });
      setUserProfile(user);
      
      return user;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/simple-auth/login`, {
        email,
        password
      });
      
      const { token: authToken, user } = response.data;
      
      // Store token, user, and activity timestamp
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('user_profile', JSON.stringify(user));
      localStorage.setItem('last_activity', Date.now().toString());
      
      setToken(authToken);
      setCurrentUser({ uid: user.uid, email: user.email });
      setUserProfile(user);
      
      return user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('last_activity');
    localStorage.removeItem('currentProject'); // Also clear any cached project data
    setToken(null);
    setCurrentUser(null);
    setUserProfile(null);
  };

  const resetPassword = async (email) => {
    // TODO: Implement password reset via email
    throw new Error('Password reset not implemented yet. Please contact support.');
  };

  const refreshProfile = async () => {
    if (token) {
      await fetchProfile(token);
    }
  };

  const getIdToken = async () => {
    return token;
  };

  const value = {
    currentUser: currentUser ? { ...currentUser, getIdToken } : null,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
